import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/integrations/ghl";
import { formatApiError } from "@/lib/api";
import { CtaButton } from "./CtaButton";
import { cn } from "@/lib/utils";

export const DISCLAIMER =
  "This is a preliminary screening only. It does not determine or guarantee eligibility. Final eligibility is determined by the appropriate child-care-assistance program.";

const STEPS = [
  { key: "child_age", title: "How old is your child?", options: ["Infant", "Toddler", "Preschool", "School-age"] },
  { key: "first_time_daycare", title: "Is this your child's first time in daycare?", options: ["Yes, first time", "No, they've been in care before"] },
  { key: "services", title: "What child care services are you looking for?", hint: "Choose all that apply.", multi: true,
    options: ["Daytime care", "Evening care", "Overnight care", "Weekend care", "Infant care", "Transportation", "Not sure yet"] },
  { key: "special_needs", title: "Does your child have any disabilities or medical needs we should know about?",
    hint: "This helps us prepare. Details can be shared privately with our team.",
    options: ["No", "Yes", "Prefer to discuss in person"] },
  { key: "household_size", title: "How many people live in your household?", options: ["2", "3", "4", "5", "6 or more"] },
  { key: "income_band", title: "What is your household's monthly income, roughly?", hint: "Broad range only — we never ask for exact amounts.",
    options: ["Under $1,500", "$1,500 – $2,999", "$3,000 – $4,499", "$4,500 – $5,999", "$6,000 or more", "Prefer not to say"] },
  { key: "parent_status", title: "Are you currently working, in school, or in training?",
    options: ["Working", "In school", "In job training", "Working and in school", "None of these right now"] },
  { key: "contact", title: "Where should RR&B follow up?" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Disclaimer() {
  return (
    <p data-testid="quiz-disclaimer" className="rrb-disclaimer">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{DISCLAIMER}</span>
    </p>
  );
}

function ContactStep({ contact, setContact }) {
  const field = (k, label, type = "text", ph = "") => (
    <label className="rrb-field">
      <span>{label}</span>
      <input data-testid={`quiz-input-${k}`} type={type} value={contact[k]} placeholder={ph}
        onChange={(e) => setContact({ ...contact, [k]: e.target.value })} />
    </label>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {field("parent_name", "Your name", "text", "First & last name")}
      {field("phone", "Phone", "tel", "Mobile number")}
      {field("email", "Email", "email", "you@example.com")}
      <label className="rrb-field">
        <span>Best time to reach you</span>
        <select data-testid="quiz-input-best_time" value={contact.best_time} onChange={(e) => setContact({ ...contact, best_time: e.target.value })}>
          {["Morning", "Afternoon", "Evening", "Late night"].map((o) => <option key={o}>{o}</option>)}
        </select>
      </label>
    </div>
  );
}

export function EligibilityQuiz({ scene, compact = false }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ parent_name: "", phone: "", email: "", best_time: "Morning" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const cur = STEPS[step];

  const choose = (v) => {
    if (cur.multi) {
      const list = answers[cur.key] || [];
      setAnswers({ ...answers, [cur.key]: list.includes(v) ? list.filter((x) => x !== v) : [...list, v] });
      return;
    }
    setAnswers({ ...answers, [cur.key]: v });
    setTimeout(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 220);
  };
  const isSelected = (o) => (cur.multi ? (answers[cur.key] || []).includes(o) : answers[cur.key] === o);
  const hasAnswer = cur.multi ? (answers[cur.key] || []).length > 0 : !!answers[cur.key];

  const submit = async () => {
    if (!contact.parent_name.trim() || contact.phone.replace(/\D/g, "").length < 7) {
      toast.error("Please add your name and a valid phone number.");
      return;
    }
    setBusy(true);
    try {
      await submitLead({ form_type: "eligibility", scene, ...answers, ...contact, email: contact.email || null });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="quiz-result" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-left">
        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
        <h3 className="mt-3 font-display text-2xl font-semibold sm:text-3xl">Thank you, {contact.parent_name.split(" ")[0]}!</h3>
        <p className="mt-2 text-base opacity-80">We received your answers. The RR&B team will follow up with you about next steps.</p>
        <div className="mt-5 flex flex-wrap gap-2">
          <CtaButton kind="tour" testId="quiz-result-tour-btn" />
          <CtaButton kind="call" testId="quiz-result-call-btn" />
        </div>
        <div className="mt-5"><Disclaimer /></div>
      </motion.div>
    );
  }

  return (
    <div data-testid="eligibility-quiz" className="text-left">
      <div className="flex items-center justify-between gap-4">
        <p className="rrb-eyebrow !mb-0">Step {step + 1} of {STEPS.length}</p>
        <div className="rrb-steps" aria-hidden>
          {STEPS.map((_, i) => <i key={i} className={cn(i <= step && "on")} />)}
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
          <h3 data-testid="quiz-step-title" className={cn("mt-3 font-display font-semibold leading-tight", compact ? "text-xl" : "text-2xl sm:text-[28px]")}>{cur.title}</h3>
          {cur.hint && <p className="mt-1 text-sm opacity-70">{cur.hint}</p>}
          <div className="mt-5">
            {cur.options ? (
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {cur.options.map((o) => (
                  <button key={o} data-testid={`quiz-option-${cur.key}-${slug(o)}`} onClick={() => choose(o)}
                    className={cn("rrb-option", isSelected(o) && "is-selected")} aria-pressed={isSelected(o)}>{o}</button>
                ))}
              </div>
            ) : (
              <ContactStep contact={contact} setContact={setContact} />
            )}
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-6 flex items-center justify-between gap-3">
        <button data-testid="quiz-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)} className="rrb-link disabled:opacity-30">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {cur.key === "contact" ? (
          <button data-testid="quiz-submit-btn" onClick={submit} disabled={busy} className="rrb-btn rrb-btn-primary">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Send my answers
          </button>
        ) : (
          hasAnswer && (
            <button data-testid="quiz-next-btn" onClick={() => setStep(step + 1)} className="rrb-link">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          )
        )}
      </div>
      {step === 0 && <div className="mt-5"><Disclaimer /></div>}
    </div>
  );
}
